/**
 * Admin — Pending Event Ticket Rewards.
 *
 * Event Tickets can be issued with a "$0 placeholder" reward: a valid voucher that proves a check-in
 * but holds no redeemable value yet (reward_state='pending'). This page lists those pending rewards
 * and lets a leadership user assign a real offer to each, which freezes that offer's value onto the
 * reward voucher (assign_ticket_reward) and flips it to 'assigned' so it can be redeemed at /redeem.
 * The public TK- ticket code never changes — only the linked voucher gains value.
 *
 * Ships effectively DORMANT: until tickets are issued with placeholder rewards, this list is empty.
 */

import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listPendingTicketRewards,
  getAssignableOffers,
  assignTicketReward,
  type PendingTicketReward,
} from "../../services/ticketRewards.service";
import { useToast } from "../../components/ui/Toast";
import Button from "../../components/common/Button";
import LoadingSpinner from "../../components/common/LoadingSpinner";

const TicketRewards: React.FC = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  // token_id -> selected offer id for that row
  const [selectedOffer, setSelectedOffer] = useState<Record<string, string>>(
    {},
  );

  const {
    data: rewards,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["pending-ticket-rewards"],
    queryFn: () => listPendingTicketRewards(),
  });

  const { data: offers } = useQuery({
    queryKey: ["assignable-offers"],
    queryFn: getAssignableOffers,
  });

  const offerLabel = useMemo(() => {
    const m = new Map<string, string>();
    (offers ?? []).forEach((o) => {
      const val =
        typeof o.value_amount === "number"
          ? o.value_type === "PERCENTAGE"
            ? `${o.value_amount}%`
            : `$${o.value_amount}`
          : "";
      m.set(o.id, `${o.name}${val ? ` (${val})` : ""}`);
    });
    return m;
  }, [offers]);

  const assignMutation = useMutation({
    mutationFn: ({ tokenId, offerId }: { tokenId: string; offerId: string }) =>
      assignTicketReward(tokenId, offerId),
    onSuccess: (res) => {
      showToast(
        "success",
        `Reward assigned — $${res.assigned_value} now redeemable.`,
      );
      queryClient.invalidateQueries({ queryKey: ["pending-ticket-rewards"] });
    },
    onError: (e: unknown) => {
      showToast(
        "error",
        e instanceof Error ? e.message : "Failed to assign reward",
      );
    },
  });

  const fmtDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Pending Ticket Rewards
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Event tickets issued with a $0 placeholder reward. Assign a real offer
          to give the reward voucher its value — the holder can then redeem it
          at <code className="text-gray-800">/redeem</code>. The ticket code
          never changes.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <div className="rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-700">
          {error instanceof Error ? error.message : "Failed to load rewards."}
        </div>
      ) : !rewards || rewards.length === 0 ? (
        <div className="rounded-md bg-gray-50 border border-gray-200 p-8 text-center text-sm text-gray-500">
          No pending ticket rewards. They appear here when tickets are issued
          with a $0 placeholder reward.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">
                  Ticket
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">
                  Event
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">
                  Recipient
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">
                  Issued
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">
                  Assign offer
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rewards.map((r: PendingTicketReward) => {
                const sel = selectedOffer[r.token_id] ?? "";
                const busy =
                  assignMutation.isPending &&
                  assignMutation.variables?.tokenId === r.token_id;
                return (
                  <tr key={r.token_id}>
                    <td className="px-4 py-3 font-mono text-xs text-gray-800">
                      {r.ticket_code}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {r.event_title ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      <div>{r.contact_name ?? "—"}</div>
                      <div className="text-xs text-gray-400">
                        {r.email ?? ""}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {fmtDate(r.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <select
                          value={sel}
                          onChange={(e) =>
                            setSelectedOffer((p) => ({
                              ...p,
                              [r.token_id]: e.target.value,
                            }))
                          }
                          className="min-w-[14rem] rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                          <option value="">Select an offer…</option>
                          {(offers ?? []).map((o) => (
                            <option key={o.id} value={o.id}>
                              {offerLabel.get(o.id)}
                            </option>
                          ))}
                        </select>
                        <Button
                          size="sm"
                          disabled={!sel || busy}
                          onClick={() =>
                            assignMutation.mutate({
                              tokenId: r.token_id,
                              offerId: sel,
                            })
                          }
                        >
                          {busy ? "Assigning…" : "Assign"}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TicketRewards;
